import {
  loadRecommendationSchema,
  type PerformedSet,
  type ReadinessCheckin,
  type SessionExercise,
  type SetPrescription,
  type TrainingBlock,
  type TrainingWeek,
  type WeekIntent,
  type WorkoutLog,
} from '@cadencia/contracts';
import {
  adjustNextSet,
  blocksExercise,
  isReadaptationPhase,
  recommendLoad,
  requireExercise,
  roundToAvailableLoad,
  sameMuscleSetsBefore,
  sorenessForMuscles,
  type CountableSet,
  type ExerciseHistory,
  type ExerciseSessionSummary,
  type LoadRecommendation,
  type RedFlag,
} from '@cadencia/domain';
import { diaLocalDe, todayLocal } from '../../lib/dates.js';
import { newSyncColumns, putNewRows, type WriteContext } from '../writes.js';
import { listAvailableLoads } from '../repositories/gymRepo.js';
import { getExerciseHistory } from '../repositories/workoutRepo.js';
import { resolveProgressionState } from '../repositories/progressionRepo.js';

/**
 * Orquesta el motor de cargas: reune los datos, llama al dominio (que es puro)
 * y persiste el snapshot de la recomendacion para el «¿Por qué?».
 */

export interface RecommendationRequest {
  userId: string;
  sessionExercise: SessionExercise;
  prescription: SetPrescription;
  totalSets: number;
  /** Series de HOY de este ejercicio, en orden. */
  performedToday: PerformedSet[];
  /**
   * Series ya registradas HOY en la sesion, de CUALQUIER ejercicio.
   *
   * Se pasan las series y no un numero porque el numero que el motor espera
   * —«series efectivas hechas hoy sobre estos mismos musculos»— es una regla de
   * dominio, y la pantalla se estaba inventando la suya: le daba el total de la
   * sesion (TRAIN-001). Quien lo traduce es `sameMuscleSetsBefore`, aqui abajo.
   */
  setsDoneToday: readonly CountableSet[];
  readiness: ReadinessCheckin | null;
  /**
   * La semana y el bloque REALES del programa, tal y como los da
   * `getSessionDetail`. No la intencion ni un booleano: si esto fueran dos
   * valores sueltos, volveria a poderse escribir `'reintroduction'` a mano,
   * que es literalmente BUG-002. `null` solo si la fila no existe.
   */
  week: TrainingWeek | null;
  block: TrainingBlock | null;
  /**
   * Banderas rojas vigentes. Con una activa que afecte a este ejercicio NO se
   * prescribe: es una regla de seguridad, no una preferencia
   * (docs/SECURITY_PRIVACY.md §6.1).
   */
  redFlags?: readonly RedFlag[];
}

/**
 * Con que se recomienda cuando la sesion no tiene semana ni bloque.
 *
 * No es un valor por defecto comodo: es el caso de base de datos incoherente,
 * que `getSessionDetail` ya ha registrado. Se elige lo CONSERVADOR —la
 * reintroduccion multiplica por 0,9 y la readaptacion fuerza doble progresion—
 * porque ante la duda se propone de menos. Lo que no se hace es aplicarlo
 * cuando los datos SI estan, que es lo que hacia el literal de la pantalla.
 */
const SIN_CONTEXTO: { intent: WeekIntent; isReadaptation: boolean } = {
  intent: 'reintroduction',
  isReadaptation: true,
};

export interface SetRecommendation extends LoadRecommendation {
  targetReps: number;
  targetRir: number;
  /** true si viene del ajuste intra-sesion en vez del calculo entre sesiones. */
  fromIntraSession: boolean;
  /**
   * La ultima sesion de ESTE ejercicio anterior a hoy, para que la pantalla
   * pueda contestar «que hice la ultima vez» sin ir a buscar nada (UX-003).
   *
   * Sale del historial que este mismo servicio ya lee para calcular la carga:
   * el dato estaba cargado y se tiraba. `null` si no hay ninguna.
   */
  previousSession: ExerciseSessionSummary | null;
}

/**
 * La ultima sesion de verdad: la mas reciente que NO sea de hoy.
 *
 * Tres decisiones explicitas, porque las tres se pueden equivocar en silencio:
 *
 *  1. `ExerciseHistory.sessions` viene de MAS ANTIGUA A MAS RECIENTE
 *     —`getExerciseHistory` ordena por `at` ascendente y se queda con las
 *     ultimas seis—, asi que la mas reciente es la del final. Se recorre hacia
 *     atras en vez de escribir `at(-1)` para poder saltar las que no sirven.
 *  2. Se descarta la de HOY. El historial incluye las series que se acaban de
 *     registrar en esta misma sesion, y llamar «la ultima vez» a la serie de
 *     hace tres minutos —que ademas ya esta pintada mas abajo— es mentira.
 *  3. El dia se compara en LOCAL con `diaLocalDe`, no con `toISOString()`.
 *     Entrenar a las 00:30 en España cae en el dia anterior en UTC, y la sesion
 *     de esta madrugada se colaria como «la ultima vez» (BUG-001).
 */
function sesionAnterior(history: ExerciseHistory): ExerciseSessionSummary | null {
  const hoy = todayLocal();
  for (let i = history.sessions.length - 1; i >= 0; i -= 1) {
    const sesion = history.sessions[i];
    if (sesion === undefined) continue;
    if (sesion.sets.length === 0) continue;
    if (diaLocalDe(sesion.at) === hoy) continue;
    return sesion;
  }
  return null;
}

export async function recommendForSet(
  request: RecommendationRequest,
): Promise<SetRecommendation> {
  const { sessionExercise, prescription } = request;
  const exercise = requireExercise(sessionExercise.exerciseSlug);
  const equipmentId = sessionExercise.equipmentId;

  // El contexto del programa se lee de las filas, no se supone.
  const weekIntent: WeekIntent = request.week?.intent ?? SIN_CONTEXTO.intent;
  const isReadaptation =
    request.block === null ? SIN_CONTEXTO.isReadaptation : isReadaptationPhase(request.block.phase);

  // Antes que cualquier calculo: con una bandera roja que afecte a este
  // ejercicio no se propone carga. Devolver `null` no es un fallo, es la
  // respuesta correcta; la pantalla muestra el aviso y no deja registrar.
  const banderas = request.redFlags ?? [];
  const perfilSeguridad = {
    muscles: [...exercise.primaryMuscles, ...exercise.secondaryMuscles],
    pattern: exercise.pattern,
  };
  if (blocksExercise(banderas, perfilSeguridad)) {
    return {
      loadKg: null,
      rawLoadKg: null,
      confidence: 0,
      reasonCodes: ['halted_by_red_flag'],
      repsAdjustment: 0,
      inputsSnapshot: {
        source: 'safety',
        redFlags: banderas.map((bandera) => bandera.code),
      },
      engineVersion: 'load_v1',
      targetReps: Math.round((prescription.targetRepsMin + prescription.targetRepsMax) / 2),
      targetRir: prescription.targetRir,
      fromIntraSession: false,
      // Con una bandera roja no se prescribe NI se registra: la pantalla
      // muestra el aviso y bloquea el boton. No se lee el historial para
      // pintar una referencia que nadie va a usar.
      previousSession: null,
    };
  }

  const availableLoads =
    equipmentId === null
      ? []
      : (await listAvailableLoads(equipmentId)).map((load) => load.loadKg);

  const regionSoreness =
    request.readiness === null
      ? null
      : sorenessForMuscles(
          [...exercise.primaryMuscles, ...exercise.secondaryMuscles],
          request.readiness.sorenessAreas,
        );

  const targetRir = prescription.targetRir;
  const centerReps = Math.round((prescription.targetRepsMin + prescription.targetRepsMax) / 2);

  // El historial, UNA vez, antes de las dos ramas.
  //
  // Estaba unas lineas mas abajo, dentro de la rama de entre sesiones. Subirlo
  // no cambia lo que ve el motor —recibe el mismo objeto— y hace que la
  // referencia de «la ultima vez» exista tambien en la serie 2 y siguientes,
  // que es cuando mas se mira. El coste es una lectura indexada por
  // `exerciseSlug` en la rama intra-sesion, que antes no la hacia; acotarla por
  // fecha es PERF-001 y no toca en este lote.
  const history = await getExerciseHistory(
    request.userId,
    sessionExercise.exerciseSlug,
    equipmentId,
  );
  const previousSession = sesionAnterior(history);

  // --- Ajuste intra-sesion: hay series previas de este ejercicio hoy ---------
  const lastSet = request.performedToday.at(-1);
  if (lastSet) {
    const firstSet = request.performedToday[0] as PerformedSet;
    const adjusted = adjustNextSet({
      lastSet: {
        setIndex: lastSet.setIndex,
        loadKg: lastSet.loadKg,
        reps: lastSet.reps,
        rir: lastSet.rir,
      },
      lastTarget: {
        repsMin: prescription.targetRepsMin,
        repsMax: prescription.targetRepsMax,
        rir: targetRir,
      },
      firstSetLoadKg: firstSet.loadKg,
      plannedNextLoadKg: lastSet.loadKg,
      nextSetIndex: prescription.setIndex,
      totalSets: request.totalSets,
      readinessScore: request.readiness?.readinessScore ?? null,
      regionSoreness,
    });

    const rounded = roundToAvailableLoad(adjusted.loadKg, availableLoads, {
      minKg: firstSet.loadKg * 0.9,
      maxKg: firstSet.loadKg * 1.1,
    });

    return {
      loadKg: rounded.loadKg ?? adjusted.loadKg,
      rawLoadKg: adjusted.loadKg,
      confidence: 0.9,
      reasonCodes: adjusted.reasonCodes,
      repsAdjustment: 0,
      inputsSnapshot: {
        source: 'intra_session',
        lastSet: { loadKg: lastSet.loadKg, reps: lastSet.reps, rir: lastSet.rir },
        target: {
          repsMin: prescription.targetRepsMin,
          repsMax: prescription.targetRepsMax,
          rir: targetRir,
        },
        factor: adjusted.factor,
        readinessToday: request.readiness?.readinessScore ?? null,
        regionSoreness,
      },
      engineVersion: 'load_v1',
      targetReps: centerReps,
      targetRir,
      fromIntraSession: true,
      previousSession,
    };
  }

  // --- Primera serie del ejercicio: calculo entre sesiones -------------------
  const smallestIncrement =
    availableLoads.length >= 2
      ? Math.min(
          ...availableLoads
            .slice(1)
            .map((value, index) => value - (availableLoads[index] as number))
            .filter((delta) => delta > 0),
        )
      : null;

  const progression = await resolveProgressionState(request.userId, {
    exerciseSlug: sessionExercise.exerciseSlug,
    equipmentId,
    equipmentKind: exercise.equipmentKind,
    smallestIncrementKg: smallestIncrement,
    currentLoadKg: history.sessions.at(-1)?.sets[0]?.loadKg ?? null,
    isReadaptation,
  });

  const recommendation = recommendLoad({
    target: {
      repsMin: prescription.targetRepsMin,
      repsMax: prescription.targetRepsMax,
      rir: targetRir,
    },
    setIndex: prescription.setIndex,
    totalSets: request.totalSets,
    setType: prescription.setType,
    history,
    progression,
    readiness: {
      todayScore: request.readiness?.readinessScore ?? null,
      regionSoreness,
    },
    block: { intent: weekIntent },
    performedToday: [],
    // Solo las que cargan los musculos de ESTE ejercicio: la cuenta la hace el
    // dominio, que es quien sabe que pesa cada tipo de serie y a que musculo va.
    sessionSetsBefore: sameMuscleSetsBefore(sessionExercise.exerciseSlug, request.setsDoneToday),
    availableLoads,
    hasConfiguredLoads: availableLoads.length > 0,
    // Para saber si se vuelve al ejercicio tras una pausa (RETURN_AFTER_PAUSE).
    today: todayLocal(),
  });

  return {
    ...recommendation,
    targetReps: centerReps + recommendation.repsAdjustment,
    targetRir,
    fromIntraSession: false,
    previousSession,
  };
}

/** Snapshot inmutable de la recomendacion: es la base del panel «¿Por qué?». */
export async function persistRecommendation(
  ctx: WriteContext,
  input: {
    workoutLog: WorkoutLog;
    sessionExerciseId: string;
    setIndex: number;
    recommendation: SetRecommendation;
    strategy: string;
  },
): Promise<void> {
  const row = loadRecommendationSchema.parse({
    ...newSyncColumns(ctx),
    workoutLogId: input.workoutLog.id,
    sessionExerciseId: input.sessionExerciseId,
    setIndex: input.setIndex,
    recommendedLoadKg: input.recommendation.loadKg,
    rawLoadKg: input.recommendation.rawLoadKg,
    targetReps: input.recommendation.targetReps,
    targetRir: input.recommendation.targetRir,
    inputs: input.recommendation.inputsSnapshot,
    strategy: input.strategy,
    reasonCodes: input.recommendation.reasonCodes,
    engineVersion: input.recommendation.engineVersion,
    confidence: input.recommendation.confidence,
  });

  await putNewRows(ctx, [{ table: 'loadRecommendations', row }]);
}
